import React, { useEffect, useState, useRef, ReactElement } from "react";

type AnimationElementProps = {
  style: React.CSSProperties;
  ref: (r: ReactElement) => ReactElement;
  onAnimationIteration: React.AnimationEventHandler<ReactElement>;
};

type AnimateUntilEndProps = {
  element: ReactElement<AnimationElementProps>;
  shouldRun: boolean;
};

/**
 * Runs current animation until its completion instead of pausing immediately
 */
export default function AnimateUntilEnd({ element, shouldRun }: AnimateUntilEndProps) {
  const [isRunning, setIsRunning] = useState(shouldRun);

  useEffect(() => {
    if (shouldRun) {
      // bulk ignoring existing errors
      // eslint-disable-next-line @web-monorepo/no-setState-in-useEffect
      setIsRunning(true);
    }
  }, [shouldRun]);

  const node = useRef<ReactElement>();

  return React.cloneElement(element, {
    style: {
      ...element.props.style,
      animationPlayState: isRunning ? "running" : "paused",
    },
    ref: (r: ReactElement) => (node.current = r),
    onAnimationIteration: (e: React.AnimationEvent<ReactElement>) => {
      if (React.isValidElement(e.target) && e.target === node.current && !shouldRun) {
        setIsRunning(false);
      }
    },
  });
}
